/**
 * oifilter.js (berkshomes)
 *
 * Purpose: Standard AJAX filtering helper used across themes.
 * Responsibilities:
 * - Build grouped FormData from #oi-filter-form inputs
 * - Disable/enable form during requests
 * - POST to /xhr/filter/{type}/ and render results, counts, maps
 * - Update URL (pushState + canonical)
 * - Expose oiFilterGetResults globally (needed by berkshomes bridge script)
 *
 * Note: The berkshomes theme uses a custom checkbox-dropdown UI.
 * The bridge script constructs hidden inputs and then calls oiFilterGetResults.
 */
let filterBatchLoader = null;

// Tracks the last path+search we knew about, so the popstate handler can
// tell real back/forward navigation apart from hash-only (anchor) jumps.
let lastPathAndSearch = window.location.pathname + window.location.search;

function update_url(resp) {
    if (window.history && "pushState" in window.history) {
        const url = resp.data.data.url;
        window.history.pushState(null, "", url);
        lastPathAndSearch = window.location.pathname + window.location.search;
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical !== null) {
            canonical.href = url;
        }
    }
}

function groupBy(objectArray, property, value) {
    return objectArray.reduce(function (acc, obj) {
        const key = obj[property];
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(obj[value]);
        return acc;
    }, {});
}

function getFormData(filter_form) {
    let formData = new FormData(filter_form);
    let object = [];
    formData.forEach(function (value, key) {
        const o = {
            name: key,
            value: value,
        };
        object.push(o);
    });
    const pieces = groupBy(object, "name", "value");
    formData = new FormData();
    for (const piece in pieces) {
        if (pieces.hasOwnProperty(piece)) {
            formData.append(piece, pieces[piece].join("|"));
        }
    }
    return formData;
}

function oiFilterDisableForm(oi_filter_form) {
    if (oi_filter_form) {
        for (let i = 0; i < oi_filter_form.elements.length; i++) {
            oi_filter_form.elements[i].setAttribute("disabled", "disabled");
        }
        const to_disable = oi_filter_form.querySelectorAll(".oi-disable-while-pending");
        to_disable.forEach(el => el.setAttribute("disabled", "disabled"));

        const model_filters = oi_filter_form.querySelectorAll(".model-filters");
        model_filters.forEach(el => el.style.opacity = "0.6");
    }
}

function oiFilterEnableForm(oi_filter_form) {
    if (oi_filter_form) {
        setTimeout(() => {
            for (let i = 0; i < oi_filter_form.elements.length; i++) {
                oi_filter_form.elements[i].disabled = false;
                oi_filter_form.elements[i].removeAttribute("disabled");
            }
            const to_disable = oi_filter_form.querySelectorAll(".oi-disable-while-pending");
            to_disable.forEach(el => {
                el.disabled = false;
                el.removeAttribute("disabled");
            });
            const model_filters = oi_filter_form.querySelectorAll(".model-filters");
            model_filters.forEach(el => el.style.opacity = "1");
        }, 500);
    }
}

function oiFilterAjaxBeforesend(oi_filter_form, from_infinite) {
    if (!from_infinite) {
        oiFilterDisableForm(oi_filter_form);
    }
}

function oiFilterGetResults(oi_filter_form, results) {
    batchLoader.forEach(b => b.abort());
    filterBatchLoader?.abort();
    beforeAjaxCompletion(oi_filter_form, results);
    let ftype = oi_filter_form.dataset.filterType;
    const url = `/xhr/filter/${ftype}/`;
    const formData = getFormData(oi_filter_form);
    const detailPage = oi_filter_form.dataset.detailPage || false;

    oi.axios({
        method: "post",
        url: url,
        data: formData,
        // Note: standard themes rely on axios to set multipart/form-data for FormData.
        // This theme explicitly sets headers elsewhere; request body remains FormData.
        headers: { "Content-Type": "application/json" },
        transformRequest: [function (data, headers) {
            // Hook that runs before the request is sent
            oiFilterAjaxBeforesend(oi_filter_form, false);
            return data;
        }],
    })
    .then(function (resp) {
        afterAjaxCompletion(results);
        if (resp.data.data.result_list) {
            if (resp.data.options) {
                const options = resp.data.options;
                for (const id in options) {
                    if (options.hasOwnProperty(id)) {
                        let el = document.getElementById(id);
                        if (el) {
                            el.innerHTML = options[id];
                        } else {
                            console.warn(`Element with ID "${id}" not found.`);
                        }
                    }
                }
            }

            if (resp.data.paginator && resp.data.paginator !== "") {
                const pageinput = oi_filter_form.querySelector('input[name="page"]');
                if (pageinput) {
                    pageinput.value = resp.data.paginator.next;
                }
            }

            if (results) {
                results.innerHTML = resp.data.data.result_list;
            }

            const oi_filter_amount = document.getElementById("oi-filter-amount");
            if (oi_filter_amount) {
                oi_filter_amount.innerHTML = resp.data.data.amount || 0;
            }

            if (resp.data.result_list_lot) {
                const results_lot = document.getElementById("oi-filter-results-lot");
                if (results_lot) {
                    results_lot.innerHTML = resp.data.result_list;
                }
            }

            if (resp.data.result_list_land) {
                const results_land = document.getElementById("oi-filter-results-land");
                if (results_land) {
                    results_land.innerHTML = resp.data.result_list;
                }
            }

            if (resp.data.map_items && resp.data.map_items !== "") {
                const map_items = document.querySelector(".oi-all-map-items");
                if (map_items) {
                    map_items.innerHTML = resp.data.map_items;
                }
            }

            update_url(resp);

            if (window.afterResultsSuccess !== undefined) {
                window.afterResultsSuccess();
            }

            // Map updates for pre-oilib pattern
            if (oi.loaded.map['oi-map'] && !detailPage) {
                // Filter types whose result cards are location cards — map
                // markers are keyed by data-listing-type="location".
                if (ftype === 'lot' || ftype === 'mlslisting' || ftype === 'past') {
                    ftype = 'location';
                }
                oi.loaded.map['oi-map'].set_markers(ftype);
                oi.loaded.map['oi-map'].fit();
            }
            const ftype_remaining = resp.data.data.ftype_remaining || [];
            filterBatchLoader = new oi.filter.LoadedBatch({
                form: oi_filter_form.id || oi_filter_form.dataset.resultsTarget,
                items: ftype_remaining,
                filterType: ftype,
                beforeLoad: () => onBeforeLoad(),
                afterLoad: () => onAfterLoad(),
                beforeBatchLoad: () => onBeforeBatchLoad(),
                afterBatchLoad: (response) => onAfterBatchLoad(response, results),
            });
            if (!ftype_remaining.length) {
                clearLoadState();
            }
        } else {
            clearLoadState();
        }
    })
    .catch(function (error) {
        afterAjaxCompletion(results);
        if (error.response) {
            if (error.response.status == 400) {
                let msg = "";
                const errors = error.response.data.errors;
                for (let err in errors) {
                    let x = errors[err];
                    msg = `${x.code} - ${x.description}\n`;
                }
                oiFilterEnableForm(oi_filter_form);
            } else if (error.response.status == 302) {
                window.location.href = error.response.data.data.redirect;
            }
        } else {
            console.error(error.message);
        }
    })
    .then(function () {
        const name_input = oi_filter_form.querySelector('input[name="m"]');
        if (name_input !== null) {
            name_input.value = "0";
        }
        oiFilterEnableForm(oi_filter_form);
    });
}

// Expose as a global so the berkshomes-filter-bridge can call it.
window.oiFilterGetResults = oiFilterGetResults;

window.addEventListener("load", () => {
    const all_oi_filter_forms = document.querySelectorAll(".oi-filter-form");

    all_oi_filter_forms.forEach((oi_filter_form) => {
        const oi_filter_clickables = oi_filter_form.querySelectorAll(".oi-filter-click");
        const oi_filter_changables = oi_filter_form.querySelectorAll(".oi-filter-change");
        const oi_filter_reset = oi_filter_form.querySelectorAll(".oi-filter-reset");
        const oi_filter_results = document.getElementById("oi-filter-results"); // Only one shared on listings page

        oi_filter_clickables.forEach((elem) => {
            elem.addEventListener("click", (e) => {
                e.preventDefault();
                oiFilterGetResults(oi_filter_form, oi_filter_results);
            });
        });

        oi_filter_changables.forEach((elem) => {
            elem.addEventListener("change", (e) => {
                e.preventDefault();
                oiFilterGetResults(oi_filter_form, oi_filter_results);
            });
        });

        if (oi_filter_reset.length && oi_filter_results) {
            oi_filter_reset.forEach((btn) => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    const to_clear = oi_filter_form.querySelectorAll(".oi-clear-on-reset");
                    to_clear.forEach((input) => {
                        if (input.type === "select-one") {
                            input.selectedIndex = 0;
                        } else if (input.id === "boyl-input") {
                            input.value = false;
                        } else if (input.type === "checkbox") {
                            input.checked = false;
                        }
                    });

                    const buttons_to_reset = oi_filter_form.querySelectorAll(".filter-button-on");
                    buttons_to_reset.forEach((btn) => {
                        btn.classList.remove("filter-button-on");
                    });

                    oiFilterGetResults(oi_filter_form, oi_filter_results);
                });
            });
        }
    });

    // Handle browser back/forward: reload so results match the URL.
    // Hash-only changes (in-page anchor links) also fire popstate but
    // should just scroll, not reload.
    window.addEventListener("popstate", () => {
        const current = window.location.pathname + window.location.search;
        if (current === lastPathAndSearch) return;
        lastPathAndSearch = current;
        location.reload();
    });

});

// --- Batch Loading ---
// Customize loading indicators and per-batch behavior here.

function addLoadingMessage() {
    // Add a loading indicator while items are being fetched.
}

function removeLoadingMessage() {
    // Remove the loading indicator after items have been fetched.
}

function beforeAjaxCompletion(filterForm, resultsContainer) {
    // Fires before the filter XHR completes (use for pre-request UI state)
    resultsContainer?.classList.add("oi-filter-loading");
}

function afterAjaxCompletion(resultsContainer) {
    // Fires after the filter XHR completes (use to restore post-request UI state)
    resultsContainer?.classList.remove("oi-filter-loading");
}

function onBeforeLoad() {
    // Fires before the first batch loads
    addLoadingMessage();
}

function dispatchItemsLoaded() {
    const itemsLoaded = new CustomEvent('oiItemsLoaded', { detail: { message: 'items loaded' } });
    document.dispatchEvent(itemsLoaded);
}

function clearLoadState() {
    removeLoadingMessage();
    dispatchItemsLoaded();
}

function onAfterLoad() {
    // Fires after every batch has loaded
    clearLoadState();
}

function onBeforeBatchLoad() {
    // Fires before each batch request
}

function onAfterBatchLoad(response, resultsContainer) {
    // Fires after each batch request
    resultsContainer?.insertAdjacentHTML('beforeend', response.data.data.result_list);
}

const batchLoader = [];

window.addEventListener('load', () => {
    document.querySelectorAll('.oi-filter-form').forEach((form) => {
        const items = JSON.parse(form.dataset.remainingItems || '[]');
        if (!items.length) {
            clearLoadState();
            return;
        }
        const resultsContainer = document.querySelector(form.dataset.resultsTarget || '#oi-filter-results');
        const loader = new oi.filter.LoadedBatch({
            items,
            form: form.id || form.dataset.resultsTarget,
            filterType: form.dataset.filterType || '',
            beforeLoad: () => onBeforeLoad(),
            afterLoad: () => onAfterLoad(),
            beforeBatchLoad: () => onBeforeBatchLoad(),
            afterBatchLoad: (response) => onAfterBatchLoad(response, resultsContainer),
        });
        batchLoader.push(loader);
    });
});
